import { differenceInMinutes, isAfter, subDays } from "date-fns";

import { classSessionSelectors, createAppSelector, selectCurrentUserId } from "./base";

import { ALL_TIME_DATA } from "components/constants";
import type { ClassSessionType } from "components/server-types";
import { ClassSessionCategory } from "components/server-types";
import { RootState } from "store";
import { getActiveClassSession, isClassSessionActive } from "utils/class-session";
import { MIN_DURATION_CLASS_MINUTES } from "utils/metrics-utils";

export const selectClassSessionBySectionId = createAppSelector(
  [classSessionSelectors.filterBySectionId],
  (classSessions) => getActiveClassSession(classSessions),
);

export const selectClassSessionCategoryBySectionId = createAppSelector(
  [selectClassSessionBySectionId],
  (classSession) => classSession?.category || null,
);

export const selectActiveClassSession = createAppSelector(
  [classSessionSelectors.filterForCurrentSection],
  (classSessions) => getActiveClassSession(classSessions),
);

export const selectActiveClassSessionId: (state: RootState) => string | null = createAppSelector(
  [selectActiveClassSession],
  (classSession: ClassSessionType | null) => classSession?.id || null,
);

export const selectActiveClassSessionCategory = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.category || null,
);

export const selectIsClassSessionInPerson = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.category === ClassSessionCategory.IN_PERSON,
);

export const selectIsClassSessionVirtual = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.category === ClassSessionCategory.VIRTUAL,
);

export const selectIsPracticeClassSession = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.is_practice || false,
);

// Separate from selectActiveClassSession just to be more specific and prevent re-renders
export const selectIsPresentationActive = createAppSelector(
  [selectActiveClassSession],
  (activeSession) => Boolean(activeSession?.presented_page_id),
);

export const selectPresentedPageId = createAppSelector(
  [selectActiveClassSession],
  (activeSession) => activeSession?.presented_page_id || null,
);

export const selectPresentingUserId = createAppSelector(
  [selectActiveClassSession],
  (activeSession) => activeSession?.presenting_user_id || null,
);

export const selectPresentingSessions = createAppSelector(
  [classSessionSelectors.selectAll, selectCurrentUserId],
  (classSessions, userId) =>
    classSessions.filter((cs) => cs.presenting_user_id === userId && isClassSessionActive(cs)),
);

export const selectClassTimerEndsAt = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.timer_ends_at || null,
);

export const selectAllVisibleClassSessionsForCurrentSection = createAppSelector(
  [classSessionSelectors.filterForCurrentSection],
  (classSessions) =>
    classSessions.filter((cs) => {
      if (cs.is_hidden_from_session_logs) return false;
      if (cs.is_practice) return false;
      if (!cs.started_at || !cs.ended_at) return false;
      const duration = differenceInMinutes(cs.ended_at, cs.started_at);
      if (duration < MIN_DURATION_CLASS_MINUTES) return false;
      return true;
    }),
);

export const selectHasVirtualSessionsForCurrentSection = createAppSelector(
  [selectAllVisibleClassSessionsForCurrentSection],
  (sessions) => sessions.some((cs) => cs.category === ClassSessionCategory.VIRTUAL),
);

export const selectFilteredClassSessionsByDateRange = createAppSelector(
  [
    selectAllVisibleClassSessionsForCurrentSection,
    (state, dateRangeValue: number | null) => dateRangeValue,
  ],
  (sessions, dateRangeValue) => {
    if (dateRangeValue == null || dateRangeValue === ALL_TIME_DATA) return sessions;
    const filterDate = subDays(new Date(), dateRangeValue);
    return sessions.filter((cs) => {
      if (!cs.started_at) return false;
      return isAfter(new Date(cs.started_at), filterDate);
    });
  },
);
